import { Button, Divider, Fade, IconButton, Stack, styled, Typography, useTheme } from "@mui/material";
import { FileIcon as FilePhosphorIcon } from "@phosphor-icons/react/dist/csr/File";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { forwardRef } from "react";
import { useSearchParams } from "react-router-dom";
import { JSX } from "react/jsx-runtime";
import DiffIcon from "../../icons/Diff.svg?react";
import { useEditorStoreContext } from "../../store/editor/EditorStoreProvider";
import { EditorTabVariant, OpenFile, selectErrorsAndWarnings } from "../../store/editor/editorStore";

const EditorTab = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.surfaceLight20.main : theme.palette.surfaceLight40.main,
  borderRadius: selected ? "12px 12px 0 0" : "none",
  minWidth: 64,
  "&. file-icon": {
    color: selected ? theme.palette.text50.main : theme.palette.text40.main,
  },
}));

const EditorTabButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: theme.palette.text50.main,
  fontWeight: 400,
  paddingRight: 0,
}));

const CloseFileIcon = styled(XIcon)({ fontSize: "0.8em" });

const FileItemIcon = styled("span", {
  shouldForwardProp: (prop) => prop !== "tabVariant",
})<{ tabVariant: EditorTabVariant }>(({ tabVariant }) => ({
  position: "relative",
  display: "flex",
  "& .compilation-info-icon": {
    position: "absolute",
    ...(tabVariant === EditorTabVariant.Editor
      ? {
          bottom: 1,
          right: 8,
        }
      : {
          bottom: -3,
          right: -3,
        }),
  },
}));
const FileIcon = ({ fileName, tabVariant }: { fileName: string; tabVariant: EditorTabVariant }) => {
  const theme = useTheme();
  const Icon = tabVariant === EditorTabVariant.Editor ? FilePhosphorIcon : DiffIcon;

  const [errors, warnings, beta] = useEditorStoreContext(selectErrorsAndWarnings(fileName));

  let iconColor: string = theme.palette.text50.main;
  let warningColor: string | undefined = undefined;

  if (warnings || beta) {
    iconColor = theme.palette.warning.main;
    warningColor = theme.palette.warning.main;
  }
  if (errors) {
    iconColor = theme.palette.error.main;
    warningColor = theme.palette.error.main;
  }

  return (
    <FileItemIcon tabVariant={tabVariant}>
      <Icon
        fontSize="large"
        className="file-icon"
        width="20px"
        height="20px"
        style={{ position: "relative" }}
        color={iconColor}
      />
      {warningColor ? (
        <WarningIcon
          size={9}
          color={warningColor}
          className="compilation-info-icon"
          data-testid={`${errors ? "error" : "warning"}-icon`}
        />
      ) : null}
    </FileItemIcon>
  );
};
const OpenedFile = forwardRef(
  (
    {
      file,
    }: {
      file: OpenFile;
    },
    ref: React.Ref<HTMLDivElement>
  ) => {
    const currentOpenFile = useEditorStoreContext((state) => state.currentOpenFile);
    const [errors, warnings, beta] = useEditorStoreContext(selectErrorsAndWarnings(file.path));
    const currentFileId = currentOpenFile?.id || "";
    const setCurrentFile = useEditorStoreContext((state) => state.setCurrentFile);
    const openFiles = useEditorStoreContext((state) => state.openFiles);
    const closeFileTab = useEditorStoreContext((state) => state.closeFileTab);
    const [searchParams, setSearchParams] = useSearchParams();
    let color = "text50.main";
    if (warnings || beta) {
      color = "accentWarning30.main";
    }
    if (errors) {
      color = "accentDanger30.main";
    }

    const handleClickTab = (file: OpenFile) => {
      setCurrentFile(file);
      setSearchParams({
        ...Object.fromEntries([...searchParams]),
        variant: file.variant,
        file: encodeURIComponent(file.path),
        fileType: file.type,
      });
    };

    const handleCloseTab = (file: OpenFile) => {
      if (searchParams.get("file") === encodeURIComponent(file.path)) {
        if (openFiles.length == 1) {
          const currentParams = Object.fromEntries([...searchParams]);
          delete currentParams.file;
          setSearchParams(currentParams);
        } else {
          const currentParams = Object.fromEntries([...searchParams]);
          const newFile = openFiles.filter((f) => f !== file)[0];
          setSearchParams({
            ...currentParams,
            variant: newFile.variant,
            file: encodeURIComponent(newFile.path),
          });
          setCurrentFile(newFile);
        }
      }
      closeFileTab(file.id);
    };
    const fileName = file.path;
    const icon: JSX.Element | null = null;

    return (
      <div ref={ref}>
        <EditorTab
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
          data-selected={file.id === currentFileId}
          selected={file.id === currentFileId}
          ref={ref}
          data-testid={`editor-tab-${fileName}`}
        >
          <EditorTabButton
            onClick={() => handleClickTab(file)}
            startIcon={icon ? <>{icon}</> : <FileIcon tabVariant={file.variant} fileName={fileName} />}
            variant="text"
            disableRipple
            data-cy="editor-tab-btn"
          >
            <Typography color={color} sx={{ whiteSpace: "nowrap" }}>
              {fileName}
            </Typography>
          </EditorTabButton>
          {file.id === currentFileId ? <Divider orientation="vertical" /> : null}
          <IconButton
            size="medium"
            onClick={() => {
              handleCloseTab(file);
            }}
            data-cy="close-file-btn"
            aria-label={`Close ${fileName}`}
          >
            <CloseFileIcon />
          </IconButton>
        </EditorTab>
      </div>
    );
  }
);
OpenedFile.displayName = "OpenedFile";

const TabContainer = styled(Stack)(({ theme }) => ({
  overflowX: "auto",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    height: "3px",
    background: theme.palette.surfaceLight20.main,
  },
  "::-webkit-scrollbar-thumb": {
    borderRadius: "5px",
    background: theme.palette.surfaceLight40.main,
  },
  backgroundColor: theme.palette.surfaceLight40.main,
  width: "100%",
  paddingTop: "2px",
}));

const EditorTabs: React.FC = () => {
  const openFiles = useEditorStoreContext((state) => state.openFiles);

  if (openFiles.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" sx={{ height: "42px", width: "100%" }}>
      <TabContainer direction="row" spacing={1} sx={{ pl: 1 }}>
        {openFiles.map((file, idx) => (
          <Fade key={idx}>
            <OpenedFile file={file} />
          </Fade>
        ))}
      </TabContainer>
    </Stack>
  );
};

export default EditorTabs;
